// Subtle shimmer-pulse placeholders. Use in place of "Loading…" text so
// the layout doesn't jump when data lands. Shapes loosely match the real
// content (booking card, list row) instead of generic gray bars.

interface SkeletonProps {
  className?: string;
}

/** Solid bar primitive. Width/height passed via className. */
export function SkeletonBar({ className = '' }: SkeletonProps) {
  return <div className={`skeleton rounded ${className}`} />;
}

/**
 * Booking-card-shaped skeleton — used on Today, Week, and CustomerDetail
 * history list. Matches the actual card so the page shape stays steady.
 */
export function SkeletonCard() {
  return (
    <div className="card flex flex-col gap-2 border-l-4 border-l-border">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-2">
          <SkeletonBar className="h-5 w-2/5" />
          <SkeletonBar className="h-3.5 w-1/3" />
          <SkeletonBar className="h-3.5 w-1/4" />
        </div>
        <div className="flex flex-col items-end gap-1.5 shrink-0">
          <SkeletonBar className="h-4 w-20" />
          <SkeletonBar className="h-3 w-24" />
          <SkeletonBar className="h-3 w-14" />
        </div>
      </div>
      <div className="flex items-center justify-between mt-1">
        <SkeletonBar className="h-5 w-16 rounded-full" />
        <SkeletonBar className="h-9 w-24 rounded-xl" />
      </div>
    </div>
  );
}

/**
 * One-row skeleton — for Customers list, Services list, Hours list.
 */
export function SkeletonRow() {
  return (
    <div className="card">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-2">
          <SkeletonBar className="h-4 w-1/2" />
          <SkeletonBar className="h-3 w-1/3" />
        </div>
        <div className="flex flex-col items-end gap-1.5 shrink-0">
          <SkeletonBar className="h-4 w-8" />
          <SkeletonBar className="h-3 w-12" />
        </div>
      </div>
    </div>
  );
}

/** Repeats a skeleton block. Default 3 — typical "few items" count. */
export function SkeletonList({
  count = 3,
  variant = 'card',
}: {
  count?: number;
  variant?: 'card' | 'row';
}) {
  const Item = variant === 'card' ? SkeletonCard : SkeletonRow;
  return (
    <div className="space-y-2">
      {Array.from({ length: count }).map((_, i) => (
        <Item key={i} />
      ))}
    </div>
  );
}
